import React, { useEffect, useState } from 'react';
import { Button, Hint } from '@vtbhome/juicyfront';
import { ModalOrderDetailedInfoProps } from './types';
import { Typography } from '@shared/ui/typography/typography';
import {
    DrawerBody,
    DrawerFooter,
    Content,
    AccessWrapper,
    FilesList,
    MembersWrapper,
    StyledTypography,
    AdminFooter,
    InitiatorWrapper,
} from './modal-order-detailed-info.styles';
import { getQueryError } from '@shared/lib/utils/get-query-error/get-query-error';
import { useOrderSharedQuery } from '@entities/order/libs/hooks/use-order-shared-query/use-order-shared-query';
import { useProductInfoMessageSharedQuery } from '@entities/product/libs/hooks/use-product-info-message-shared-query/use-user-products-list-shared-query';
import { useOrderFilesSharedQuery } from '@entities/order/libs/hooks/use-order-files-shared-query/use-order-files-shared-query';
import { useCurrentUserSelector } from '@entities/current-user/model/selectors';
import { getCurrentUserRole } from '@shared/lib/utils/get-current-user-role/get-current-user-role';
import { NetworkStateNew } from '@shared/ui/network-state-new/network-state-new';
import { MESSAGE_REQUEST_DEFAULT_PARAMETERS } from './constants';
import { MESSAGE_ORDER_STATUS_MAP, HINT_STATUS_MAP } from './modal-order.mixins';
import { UserRoles } from '@shared/model/user';
import { userName } from '@shared/lib/utils/user-name/user-name';
import { MemberOrderRequest } from '@entities/request/ui/member-order-request/member-order-request';
import { ModalCommonData } from './ui/modal-common-data/modal-common-data';
import { ModalAdminData } from './ui/modal-admin-data/modal-admin-data';
import { getOrderInitiator } from './libs/get-order-initiator/get-order-initiator';
import { InitiatorUser } from '@features/user/initiator-user/initiator-user';
import { Modal } from '@shared/ui/modal/modal';
import { File } from '@shared/ui/file/file';
import { forceDownloadByUrl } from '@shared/lib/utils/force-download-by-url/force-download-by-url';

export const ModalOrderDetailedInfo: React.FC<ModalOrderDetailedInfoProps> = ({ isShow, orderId, onClose }) => {
    const user = useCurrentUserSelector();
    const isManager = getCurrentUserRole(user) === UserRoles.Manager;
    const isAdmin = getCurrentUserRole(user) === UserRoles.Admin;

    const [getOrderInfo, getOrderInfoResult] = useOrderSharedQuery(orderId || 0);
    const isLoading =
        getOrderInfoResult.isLoading || getOrderInfoResult.isFetching || getOrderInfoResult.isUninitialized;
    const error = getQueryError(getOrderInfoResult);
    const orderData = getOrderInfoResult?.data;

    const [parameters, setParameters] = useState(MESSAGE_REQUEST_DEFAULT_PARAMETERS);
    const [getMessage, getMessageResult] = useProductInfoMessageSharedQuery(parameters);
    const isLoadingMessage = getMessageResult.isLoading || getMessageResult.isFetching;
    const messageText = getMessageResult?.data;

    const [getOrderFiles, getOrderFilesResult] = useOrderFilesSharedQuery(orderId || 0);
    const isLoadingFiles = getOrderFilesResult.isLoading || getOrderFilesResult.isFetching;
    const filesData = getOrderFilesResult?.data;

    const [isModalComplete, setIsModalComplete] = useState(false);
    const [isModalReject, setIsModalReject] = useState(false);

    const { title: sectionInitiatorTitle, initiator, isInitiatorEmployee } = getOrderInitiator(orderData);
    const showBankDetails = Boolean(Number(orderData?.product?.price));
    const showMembers = (isAdmin && !isInitiatorEmployee) || (isManager && orderData?.status !== 'order_forming');

    useEffect(() => {
        if (isShow && orderId) {
            getOrderInfo(orderId).then(({ isSuccess, data }) => {
                if (isSuccess) {
                    const messageType = MESSAGE_ORDER_STATUS_MAP[data?.status];
                    if (messageType) {
                        setParameters({
                            id: data?.product?.id,
                            messageType,
                        });
                    }
                }

                if (data?.status === 'completed' && data?.requests?.some((request) => request?.user?.id === user?.id)) {
                    getOrderFiles(orderId || 0);
                }
            });
        }
    }, [isShow, orderId]);

    useEffect(() => {
        if (
            isShow &&
            (orderData?.status === 'order_reject' || orderData?.status === 'completed') &&
            orderData?.requests?.some((request) => request?.user?.id === user?.id) &&
            Boolean(parameters?.id)
        ) {
            getMessage(parameters);
        }
    }, [isShow, parameters]);

    useEffect(() => {
        if (!isShow) {
            setParameters(MESSAGE_REQUEST_DEFAULT_PARAMETERS);
        }
    }, [isShow]);

    return (
        <Modal
            isShow={isShow}
            variant='drawer'
            size='l'
            header={<Typography size='h6'>{orderData?.product?.name}</Typography>}
            footer={
                <>
                    {isAdmin && orderData?.status === 'on_payment' ? (
                        <AdminFooter>
                            <Button buttonType='danger' onClick={() => setIsModalReject(true)}>
                                Отменить оплату
                            </Button>
                            <Button buttonType='primary' onClick={() => setIsModalComplete(true)}>
                                Подтвердить оплату
                            </Button>
                        </AdminFooter>
                    ) : (
                        <DrawerFooter>
                            <Button buttonType='light' onClick={onClose}>
                                Закрыть
                            </Button>
                        </DrawerFooter>
                    )}
                </>
            }
            onClose={onClose}>
            <DrawerBody>
                <NetworkStateNew
                    size='s'
                    isLoading={isLoading || isLoadingMessage || isLoadingFiles}
                    isError={Boolean(error)}
                    isInitialized={Boolean(orderData) && orderId === getOrderInfoResult.data?.id}
                    isFiltered={false}
                    hasData={Boolean(orderData)}
                    errorProps={{
                        title: 'Ошибка загрузки сделки',
                        description: error?.message,
                    }}>
                    {orderData && (
                        <Content>
                            <ModalCommonData
                                orderData={orderData}
                                orderId={orderId}
                                showBankDetails={showBankDetails}
                                goToBuyOrderRoute={isManager && orderData.status === 'order_forming'}
                            />

                            {Boolean(parameters.id) && !isLoadingMessage && messageText?.message && (
                                <AccessWrapper>
                                    <Typography color='neutral_90' size='h6'>
                                        Доступ к продукту
                                    </Typography>

                                    <Hint title={messageText?.message} variant={HINT_STATUS_MAP[orderData?.status]} />
                                </AccessWrapper>
                            )}

                            {filesData && (
                                <FilesList>
                                    {filesData?.map((file) => (
                                        <File
                                            key={file?.id}
                                            isLoading={isLoadingFiles}
                                            uploadedFile={{
                                                url: file?.url,
                                                id: file?.id,
                                                name: file?.name,
                                                extension: file?.meta?.extension,
                                                size: file?.meta?.size,
                                            }}
                                            onSave={() => forceDownloadByUrl(file?.url, file?.name)}
                                        />
                                    ))}
                                </FilesList>
                            )}

                            {isAdmin && initiator && (
                                <InitiatorWrapper>
                                    <StyledTypography color='neutral_90' size='h6'>
                                        {sectionInitiatorTitle}
                                    </StyledTypography>

                                    <InitiatorUser
                                        firstName={initiator?.firstName}
                                        lastName={initiator?.lastName}
                                        middleName={initiator?.middleName}
                                        organizationPost={initiator?.jobTitle}
                                        avatarUrl={initiator?.photoUrl}
                                        tooltipData={{
                                            department: initiator?.department,
                                            email: initiator?.email,
                                            phoneNumber: initiator?.phoneNumber,
                                        }}
                                    />
                                </InitiatorWrapper>
                            )}

                            {showMembers && (
                                <MembersWrapper>
                                    <StyledTypography color='neutral_90' size='h6'>
                                        Участники сделки
                                    </StyledTypography>

                                    {orderData?.requests?.map((request) => {
                                        const memberName = userName({
                                            firstName: request?.user?.firstName,
                                            lastName: request?.user?.lastName,
                                            middleName: request?.user?.middleName,
                                        });

                                        return (
                                            <MemberOrderRequest
                                                key={request.id}
                                                photoUrl={request?.user?.photoUrl}
                                                nameShort={memberName.short}
                                                nameFull={memberName.full}
                                                jobTitle={request?.user?.jobTitle}
                                                quantity={request?.quantity}
                                            />
                                        );
                                    })}
                                </MembersWrapper>
                            )}
                        </Content>
                    )}
                </NetworkStateNew>
            </DrawerBody>

            <ModalAdminData
                orderId={orderId}
                isModalComplete={isModalComplete}
                setIsModalComplete={setIsModalComplete}
                isModalReject={isModalReject}
                setIsModalReject={setIsModalReject}
            />
        </Modal>
    );
};
